import { useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search } from "lucide-react";
import { toast } from "sonner";
import { PageHead, Reveal, Eyebrow } from "@/components/Motion";
import { Loading, useApi } from "@/components/Common";
import { api, errText, media } from "@/lib/api";
import { useSite } from "@/context/SiteContext";

const TABS = [["members", "Members"], ["archive", "Archive"], ["associations", "Associations"], ["memory", "Add a Memory"]];

const Members = ({ data }) => {
  const [q, setQ] = useState("");
  const [branch, setBranch] = useState("all");
  const members = data.members || [];
  const branches = [...new Set(members.map((m) => m.branch).filter(Boolean))];
  const byId = Object.fromEntries(members.map((m) => [m.id, m]));
  const list = members.filter((m) => (branch === "all" || m.branch === branch) && `${m.name} ${m.born} ${m.died || ""} ${m.branch}`.toLowerCase().includes(q.toLowerCase()));
  const bizOf = (id) => (data.businesses || []).filter((b) => (b.roles || []).some((r) => r.member === id));
  return (
    <>
      <div className="flex flex-col md:flex-row gap-3 mb-10">
        <div className="relative flex-1"><Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#C9A24B]" /><input className="field !pl-11" placeholder="Search by name, year or branch" value={q} onChange={(e) => setQ(e.target.value)} data-testid="family-search-input" /></div>
        <select className="field md:w-64" value={branch} onChange={(e) => setBranch(e.target.value)} data-testid="family-branch-filter"><option value="all">All branches</option>{branches.map((b) => <option key={b}>{b}</option>)}</select>
      </div>
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {list.map((m, i) => (
          <Reveal key={m.id} delay={(i % 3) * 0.06} className="card-gold overflow-hidden" data-testid={`member-profile-${i}`}>
            <div className="aspect-[16/10] overflow-hidden">{m.photo_url ? <img src={media(m.photo_url)} alt={m.name} className="w-full h-full object-cover grayscale hover:grayscale-0 transition-[filter] duration-1000" />
              : <div className="w-full h-full grid place-items-center bg-[radial-gradient(ellipse_at_center,#163B29,#0B1D14)]" data-testid={`member-placeholder-${i}`}><span className="font-display text-5xl text-[#C9A24B]">{m.name.replace(/"[^"]*"/, "").trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("")}</span><span className="absolute mt-24 text-[11px] text-[#F6EFE0]/70">Photo coming soon</span></div>}</div>
            <div className="p-6">
              <Eyebrow>Generation {m.generation} · {m.branch}</Eyebrow>
              <p className="mt-2 font-display text-2xl">{m.name}</p>
              <p className="text-sm text-[#E4C87F]">{m.born}{m.died ? ` – ${m.died}` : ""}</p>
              <p className="mt-3 text-sm text-[#F6EFE0]/85">{m.bio}</p>
              <div className="mt-4 text-xs text-[#F6EFE0]/75 space-y-1">
                {m.parents?.length > 0 && <p>Parents: {m.parents.map((p) => byId[p]?.name).filter(Boolean).join(", ") || "Private"}</p>}
                {m.spouse && <p>Spouse: {byId[m.spouse]?.name || "Private"}</p>}
                {members.some((c) => c.parents?.includes(m.id)) && <p>Children: {members.filter((c) => c.parents?.includes(m.id)).map((c) => c.name).join(", ")}</p>}
                {bizOf(m.id).length > 0 && <p>Businesses: {bizOf(m.id).map((b) => `${b.name} (${b.roles.find((r) => r.member === m.id)?.role})`).join(", ")}</p>}
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </>
  );
};

const Archive = ({ data }) => (
  <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
    {[...(data.archive || []), ...(data.timeline || [])].map((a, i) => (
      <Reveal key={a.id} delay={(i % 3) * 0.05} className="card-gold overflow-hidden" data-testid={`archive-item-${i}`}>
        <img src={media(a.photo_url)} alt={a.title} className="w-full aspect-[16/10] object-cover sepia-[.5]" />
        <div className="p-6"><Eyebrow>{a.kind || a.year}</Eyebrow><p className="mt-2 font-display text-xl">{a.title}</p><p className="mt-2 text-sm text-[#F6EFE0]/80">{a.note || a.story}</p></div>
      </Reveal>
    ))}
  </div>
);

const Roots = ({ data }) => {
  const assoc = data.associations || [];
  const members = data.members || [];
  const used = members.filter((m) => assoc.some((a) => (a.members || []).includes(m.id)));
  const W = 1000; const H = 520;
  const ax = (i) => 80 + (i * (W - 160)) / Math.max(1, assoc.length - 1);
  const mx = (i) => 60 + (i * (W - 120)) / Math.max(1, used.length - 1);
  return (
    <div className="card-gold p-4 md:p-8 overflow-x-auto" data-testid="associations-roots">
      <svg viewBox={`0 0 ${W} ${H}`} className="min-w-[760px] w-full">
        {assoc.flatMap((a, i) => (a.members || []).map((mid) => { const j = used.findIndex((u) => u.id === mid); if (j < 0) return null; return (
          <motion.path key={`${a.id}${mid}`} d={`M${ax(i)} 90 C ${ax(i)} 260, ${mx(j)} 260, ${mx(j)} 430`} stroke="#C9A24B" strokeOpacity=".5" fill="none" strokeWidth="1.4" initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true, amount: 0.1 }} transition={{ duration: 1.6, delay: i * 0.1 }} />); }))}
        {assoc.map((a, i) => <g key={a.id}><circle cx={ax(i)} cy="80" r="10" fill="#C9A24B" /><text x={ax(i)} y="50" textAnchor="middle" fill="#F6EFE0" fontSize="13" fontFamily="Playfair Display">{a.name}</text><text x={ax(i)} y="34" textAnchor="middle" fill="#E4C87F" fontSize="10">{a.kind}</text></g>)}
        {used.map((m, j) => <g key={m.id}><circle cx={mx(j)} cy="440" r="7" fill="#0F2A1D" stroke="#C9A24B" /><text x={mx(j)} y="470" textAnchor="middle" fill="#F6EFE0" fontSize="11">{m.name.split(" ")[0]}</text></g>)}
      </svg>
    </div>
  );
};

const AddMemory = () => {
  const [f, setF] = useState({ title: "", year: "", author: "", story: "" });
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/family/memories", f); toast.success("Thank you. Your memory is waiting for approval."); setF({ title: "", year: "", author: "", story: "" }); }
    catch (er) { toast.error(errText(er)); }
    setBusy(false);
  };
  return (
    <form onSubmit={send} className="card-gold p-6 md:p-10 grid gap-5 max-w-3xl" data-testid="add-memory-form">
      <p className="font-accent italic text-xl text-[#E4C87F]">Share a memory. It will be held for approval before it is published.</p>
      <div className="grid gap-5 md:grid-cols-3">
        <div className="md:col-span-2"><label className="label">Title</label><input className="field" required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="memory-title-input" /></div>
        <div><label className="label">Year</label><input className="field" type="number" value={f.year} onChange={(e) => setF({ ...f, year: e.target.value })} data-testid="memory-year-input" /></div>
      </div>
      <div><label className="label">Your name</label><input className="field" value={f.author} onChange={(e) => setF({ ...f, author: e.target.value })} data-testid="memory-author-input" /></div>
      <div><label className="label">The memory</label><textarea className="field min-h-[160px]" required value={f.story} onChange={(e) => setF({ ...f, story: e.target.value })} data-testid="memory-story-input" /></div>
      <button className="btn-gold w-fit" disabled={busy} data-testid="memory-submit-btn">{busy ? "Sending..." : "Send for approval"}</button>
    </form>
  );
};

export default function Family() {
  const { session, logout } = useSite() || {};
  const [tab, setTab] = useState("members");
  const { data, loading, error } = useApi("/family/overview", [session?.active]);
  const view = useMemo(() => ({ members: Members, archive: Archive, associations: Roots }), []);
  if (session && !session.active) return <Navigate to="/gate" replace />;
  if (error === 403) return <Navigate to="/gate" replace />;
  const V = view[tab];
  return (
    <div data-testid="family-page">
      <PageHead eyebrow="Family Members Only" title="Inside the family archive" line="Everything here is family only. Please keep it that way." />
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-28">
        <div className="flex flex-wrap items-center gap-2 mb-12">
          {TABS.map(([k, l]) => <button key={k} onClick={() => setTab(k)} className={`btn-sm rounded-full border transition-colors ${tab === k ? "bg-[#C9A24B] text-[#0B1D14] border-[#C9A24B]" : "border-[#C9A24B]/40 hover:border-[#C9A24B]"}`} data-testid={`family-tab-${k}`}>{l}</button>)}
          <Link to="/tree" className="btn-sm rounded-full border border-[#C9A24B]/40 hover:border-[#C9A24B]" data-testid="family-link-tree">Full tree</Link>
          <Link to="/enterprise" className="btn-sm rounded-full border border-[#C9A24B]/40 hover:border-[#C9A24B]" data-testid="family-link-enterprise">Businesses</Link>
          <Link to="/memoriam" className="btn-sm rounded-full border border-[#C9A24B]/40 hover:border-[#C9A24B]" data-testid="family-link-memoriam">Remember</Link>
          <button onClick={logout} className="ml-auto btn-sm rounded-full border border-[#C9A24B]/40 hover:border-[#C9A24B]" data-testid="family-logout-btn">Log out</button>
        </div>
        {loading || !data ? <Loading /> : tab === "memory" ? <AddMemory /> : <V data={data} />}
      </section>
    </div>
  );
}
